import React, { useMemo } from 'react';

import type { DocNode } from '@atlaskit/adf-schema';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { type JSONNode, JSONTransformer } from '@atlaskit/editor-json-transformer';
import { Fragment, type Node, type Schema } from '@atlaskit/editor-prosemirror/model';
import { ReactRenderer } from '@atlaskit/renderer';
import { RendererActionsContext } from '@atlaskit/renderer/actions';

import { AgentManagedExtensionView } from '../common/AgentManagedExtensionView';

type NodeContent = {
	attrs?: Record<string, unknown>;
	content?: NodeContent[];
	marks?: Array<{ attrs?: Record<string, unknown>; type: string }>;
	text?: string;
	type: string;
};

/**
 * Loose `node` shape compatible with both ProseMirror `Node`s (passed by the custom nodeView) and
 * ADF JSON nodes (passed by the manifest's render function). Only `attrs` and `content` are read.
 */
interface Props {
	// Set to true when this block is the current ProseMirror NodeSelection
	isSelected?: boolean;
	node: { attrs?: Record<string, unknown>; content?: unknown };
	providerFactory?: ProviderFactory;
	/** When true, renders only the body content without interactive chrome (hover border, label). */
	readOnly?: boolean;
}

const jsonTransformer = new JSONTransformer();

// Rebuild ADF body content as ProseMirror nodes
const createNodesFromContent = (
	content: readonly NodeContent[],
	schema: Schema,
): readonly Node[] => {
	const nodes: Node[] = [];
	for (const node of content) {
		if (node?.type === 'text') {
			const marks = (node.marks ?? [])
				.map((m) => (schema.marks[m.type] ? schema.marks[m.type].create(m.attrs) : null))
				.filter((m): m is NonNullable<typeof m> => m !== null);
			nodes.push(schema.text(node.text ?? '', marks));
		} else if (schema.nodes[node.type] && node.content) {
			const contentNodes = createNodesFromContent(node.content, schema);
			nodes.push(schema.nodes[node.type].create(node.attrs, contentNodes));
		}
	}
	return nodes;
};

/**
 * Read-only renderer for the agent-managed bodied extension. Body content is supplied via
 * `node.content` (ADF JSON or PM `Fragment`) and rendered with `<ReactRenderer>`. Mirrors
 * the pattern used by `AIPanelRenderer`.
 */
export const AgentManagedBodiedExtensionRenderer = ({
	node,
	providerFactory,
	isSelected,
	readOnly,
}: Props): JSX.Element | null => {
	const doc: DocNode = useMemo(() => {
		let content: JSONNode[];
		if (Array.isArray(node.content)) {
			content = node.content;
		} else if (node.content instanceof Fragment) {
			// Already PM nodes — encode directly to ADF JSON
			content = [];
			node.content.forEach((child) => {
				content.push(jsonTransformer.encodeNode(child));
			});
		} else {
			content = [{ type: 'paragraph' }];
		}
		// This is already valid ADF — just wrap it as a doc
		return { version: 1, type: 'doc', content } as DocNode;
	}, [node.content]);

	return (
		<AgentManagedExtensionView isSelected={isSelected} readOnly={readOnly}>
			<RendererActionsContext>
				<ReactRenderer document={doc} appearance="full-width" dataProviders={providerFactory} />
			</RendererActionsContext>
		</AgentManagedExtensionView>
	);
};
